import type { ComponentType } from 'react';
import { useState, useEffect } from 'react';
import { Illustration, Stack } from '@kiwicom/orbit-components';
import {
  Heading,
  Text,
  Button,
  AccountLayout,
  InclusivelyHidden,
  Dialog,
} from '@kiwicom/account-components';
import { useAuth } from '@kiwicom/nitro/lib/services/auth/context';
import { isBrowser } from '@adeira/js';
import { FormattedMessage } from 'react-intl';
import { useLog, events } from '@kiwicom/account-tracking';

import useRedirectOnLoggedIn from './useRedirectOnLoggedIn';
import { getToken } from '../util';

type Props = {
  readonly reason: string | null | undefined;
};

const Login = ({ reason }) => {
  const [isDialogOpened, openDialog] = useState(false);
  const { onOpenModal } = useAuth();
  const { log } = useLog();
  const token = getToken();
  useRedirectOnLoggedIn();

  // "onOpenModal" accept additional modal props
  // See "Modal" type https://kiwi.wiki/frontend/nitrolib/services/#context
  const onClick = () => onOpenModal();

  useEffect(() => {
    if (reason === 'noBookingAccess') {
      openDialog(true);
      void log(events.accountLogin.NO_BOOKING_ACCESS, {});
      return;
    }

    onOpenModal();
  }, [reason, log, onOpenModal]);
  return (
    <AccountLayout>
      <Stack
        spacing="XXLarge"
        flex
        direction="column"
        align="center"
        tablet={{
          direction: 'row',
        }}
      >
        <InclusivelyHidden>
          <Heading as="h1">
            <FormattedMessage
              id="account_v2.login.screen_reader_only.title"
              defaultMessage="Sign in"
            />
          </Heading>
        </InclusivelyHidden>
        <Illustration name="Offline" />
        <div>
          <Stack spacing="small">
            <Heading spaceAfter="small" type="title2" as="h2">
              <FormattedMessage
                id="account_v2.login.title"
                defaultMessage="Sign in to visit your account"
              />
            </Heading>
            <Stack spacing="XLarge">
              <Text type="secondary">
                <FormattedMessage
                  id="account_v2.login.description"
                  defaultMessage="You'll be able to access your trips, price alerts, and settings."
                />
              </Text>
              <Button
                loading={isBrowser() === true ? token != null : false}
                dataTest="SignInButton"
                onClick={onClick}
              >
                <FormattedMessage id="account_v2.login.sign_in" defaultMessage="Sign in" />
              </Button>
            </Stack>
          </Stack>
        </div>
      </Stack>
      <Dialog
        illustration={<Illustration size="extraSmall" name="NoBookings" />}
        isVisible={isDialogOpened}
        title={
          <FormattedMessage
            id="account_v2.login.dialog.no_booking_access.title"
            defaultMessage="You don't have access to this booking"
            description="Screenshot: login.dialog.no_booking_access.title.jpg"
          />
        }
        description={
          <FormattedMessage
            id="account_v2.login.dialog.no_booking_access.description"
            defaultMessage="We're sorry, but you don't have access to this booking. Please sign in with the correct email address."
            description="Screenshot: login.dialog.no_booking_access.description.jpg"
          />
        }
        primaryAction={<Button onClick={() => openDialog(false)}>Continue</Button>}
      />
    </AccountLayout>
  );
};

export default Login as ComponentType<Props>;
